import { parseUtcTimestamp } from "./parseUtcTimestamp"

/**
 * Converts age in seconds to a human-readable relative time string.
 */
export function formatAge(ageSeconds: number): string {
  if (ageSeconds < 60) return "just now"
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`
  const hours = Math.floor(ageSeconds / 3600)
  const mins = Math.floor((ageSeconds % 3600) / 60)
  return mins > 0 ? `${hours}h ${mins}m ago` : `${hours}h ago`
}

const MILLISECONDS_PER_DAY = 86_400_000
const SECONDS_PER_DAY = 86_400

/**
 * A staged vendor file's timestamp: the exact moment first, then how long ago
 * in brackets — "Aug 12, 9:46 AM (4h ago)".
 *
 * The exact time is the load-bearing half. An operator uploads today's exports
 * and needs to know the file in the slot IS today's; "28h ago" reads as recent
 * at a glance while actually meaning yesterday, which is the failure this
 * replaces. The relative half stays because it is the faster read when the file
 * genuinely is minutes old.
 *
 * `formatAge` tops out in hours, so anything past a day counts in days here
 * rather than reporting "52h ago".
 */
export function formatUploadedAt(timestamp: string | null | undefined): string {
  if (!timestamp) return ""
  const when = parseUtcTimestamp(timestamp)
  if (Number.isNaN(when.getTime())) return ""

  const stamp = when.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
  const seconds = Math.max(0, (Date.now() - when.getTime()) / 1000)
  const days = Math.floor(seconds / SECONDS_PER_DAY)
  const relative =
    days >= 1 ? `${days}d ago` : formatAge(seconds)
  return `${stamp} (${relative})`
}

/** Elapsed run time as m:ss — "1:23". Counts up, so it is visibly alive. */
export function formatElapsed(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safe / 60)
  return `${minutes}:${String(safe % 60).padStart(2, "0")}`
}

/**
 * Whole days between an ISO `yyyy-mm-dd` calendar date and today, or null when
 * the string is absent or unparseable.
 *
 * Both sides are reduced to LOCAL midnight before subtracting. `new Date("2026-06-30")`
 * would parse as UTC midnight instead, which is still the previous day for
 * anyone west of Greenwich — Dolan is in Nevada, so dates would read a day old
 * for most of the working day. Reducing both sides the same way also makes the
 * subtraction offset-free, so the rounding below only ever absorbs a
 * daylight-saving hour, never a timezone.
 */
export function daysSinceDate(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null
  const [year, month, day] = isoDate.split("-").map(Number)
  if (!year || !month || !day) return null
  const then = new Date(year, month - 1, day)
  if (Number.isNaN(then.getTime())) return null
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  // Midnight-to-midnight and rounded, so a daylight-saving boundary inside the
  // span cannot shift the count by a day.
  return Math.round((today.getTime() - then.getTime()) / MILLISECONDS_PER_DAY)
}

/**
 * The worklist's DETAIL cell: a problem's stored status word plus how long ago
 * its date was — "Sold 43 days ago".
 *
 * The server stores the date and never the age, so that a saved run is not
 * rewritten as time passes; the age is composed here and is therefore correct
 * whenever the page is read. Falls back to the bare detail when there is no
 * date, and when the date is in the future — a vendor typo should not produce
 * "Sold -3 days ago".
 */
export function formatProblemDetail(
  detail: string | null | undefined,
  isoDate: string | null | undefined,
): string | null {
  if (!detail) return null
  const days = daysSinceDate(isoDate)
  if (days === null || days < 0) return detail
  if (days === 0) return `${detail} today`
  if (days === 1) return `${detail} yesterday`
  return `${detail} ${days} days ago`
}

/**
 * A departure younger than this may not be a departure at all.
 *
 * Tekion bookings get reversed onto the SAME stock number — the car never left
 * the lot, the sold date is simply cleared — and a single export carries no
 * trace of it. Measured against a later export: 2.18% of departures unwound
 * inside 7 days, 0.95% at 15-30 days, and 0.00% of 152,368 past 120 days. All
 * five demonstrated cases were 2-5 days out.
 *
 * This is a reason to say "check first", NOT to suppress the row. Every
 * suppression rule tested cost more true positives than it saved: a 7-day
 * quarantine caught 4 of 5 bad rows while holding back 182 of 233 good ones,
 * and no field distinguishes an unwound deal from a real one at flag time —
 * the site and Tekion agree on condition, mileage and stock number, because it
 * is the same record.
 */
export const REVERSIBLE_DEPARTURE_DAYS = 7

/**
 * How long a problem has been open, from the UTC timestamp INVI first saw it:
 * "today", "1 day", "12 days". Shared by the critical group header
 * ("oldest 53 days") and the critical row tag, so the two can never
 * disagree. `now` is a parameter so tests need no clock.
 */
export function formatDaysOpen(firstSeenAt: string, now: Date = new Date()): string {
  const elapsed = now.getTime() - parseUtcTimestamp(firstSeenAt).getTime()
  const days = Math.floor(elapsed / MILLISECONDS_PER_DAY)
  if (days < 1) return "today"
  return days === 1 ? "1 day" : `${days} days`
}

/** True when a departure is fresh enough that the deal may still reverse. */
export function isReversibleDeparture(isoDate: string | null | undefined): boolean {
  const days = daysSinceDate(isoDate)
  return days !== null && days >= 0 && days < REVERSIBLE_DEPARTURE_DAYS
}
